import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FormPage } from '@/ui/FormPage';
import { FormSection } from '@/ui/FormSection';
import { FormFieldWrapper } from '@/ui/FormField';
import { Input } from '@/ui/base/input';
import { Textarea } from '@/ui/base/textarea';
import { Switch } from '@/ui/base/switch';
import { Label } from '@/ui/base/label';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { EmptyState } from '@/ui/EmptyState';
import { usePortalPagina, usePortalSecoes, type PortalSecao } from '@/lib/usePortalContent';
import { avisarAcaoIndisponivel } from '@/modulos/admin/portal-publico/avisoIndisponivel';
import { Globe, Search, ChevronDown, Plus, Pencil, Trash2, Code2, FileJson } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/nucleo/utilitarios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/ui/base/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';

function CharCounter({ value, max }: { value: string; max: number }) {
  const len = value?.length || 0;
  return (
    <span
      className={cn(
        'text-[10px] tabular-nums',
        len > max ? 'text-destructive font-medium' : 'text-muted-foreground/80',
      )}
    >
      {len}/{max}
    </span>
  );
}

function SerpPreview({
  title,
  description,
  slug,
}: {
  title: string;
  description: string;
  slug: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4 space-y-1">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
        Preview Google
      </p>
      <p className="text-sm text-info truncate">{`teg.app-mtechnologia.com › portal › ${slug}`}</p>
      <p className="text-base text-primary font-medium leading-snug truncate">
        {title || 'Título da página'}
      </p>
      <p className="text-xs text-muted-foreground line-clamp-2">
        {description || 'Descrição da página...'}
      </p>
    </div>
  );
}

// ── JSON-LD Templates ───────────────────────────────────────────
const JSONLD_TEMPLATES: Record<string, { label: string; template: JsonLdEntry }> = {
  Organization: {
    label: 'Organization',
    template: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Transporte Escolar Gratuito — Prefeitura de São Paulo',
      url: 'https://teg.app-mtechnologia.com/portal',
      logo: 'https://teg.app-mtechnologia.com/favicon.ico',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+55-11-156',
        contactType: 'customer service',
        areaServed: 'BR',
        availableLanguage: 'Portuguese',
      },
    },
  },
  FAQPage: {
    label: 'FAQPage',
    template: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Como funciona o TEG?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'O TEG é o Transporte Escolar Gratuito da Prefeitura de São Paulo...',
          },
        },
        {
          '@type': 'Question',
          name: 'Quem pode solicitar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Famílias com alunos matriculados na rede municipal de ensino...',
          },
        },
      ],
    },
  },
  BreadcrumbList: {
    label: 'BreadcrumbList',
    template: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'TEG',
          item: 'https://teg.app-mtechnologia.com/portal',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Página',
          item: 'https://teg.app-mtechnologia.com/portal/slug',
        },
      ],
    },
  },
  GovernmentOrganization: {
    label: 'GovernmentOrganization',
    template: {
      '@context': 'https://schema.org',
      '@type': 'GovernmentOrganization',
      name: 'Secretaria Municipal de Mobilidade Urbana e Transporte — Prefeitura de São Paulo',
      url: 'https://www.prefeitura.sp.gov.br/smt',
      parentOrganization: { '@type': 'GovernmentOrganization', name: 'Prefeitura de São Paulo' },
    },
  },
};

type JsonLdEntry = Record<string, unknown>;

function JsonLdEditor({
  value,
  onChange,
}: {
  value: JsonLdEntry[];
  onChange: (v: JsonLdEntry[]) => void;
}) {
  const [raw, setRaw] = useState(JSON.stringify(value || [], null, 2));
  const [error, setError] = useState('');

  useEffect(() => {
    setRaw(JSON.stringify(value || [], null, 2));
  }, [value]);

  const handleBlur = () => {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setError('Deve ser um array JSON');
        return;
      }
      setError('');
      onChange(parsed);
    } catch {
      setError('JSON inválido');
    }
  };

  const addTemplate = (key: string) => {
    const tpl = JSONLD_TEMPLATES[key];
    if (!tpl) return;
    const current = value || [];
    const updated = [...current, tpl.template];
    onChange(updated);
    setRaw(JSON.stringify(updated, null, 2));
    setError('');
  };

  const itemCount = (value || []).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Adicionar template:</span>
        {Object.entries(JSONLD_TEMPLATES).map(([key, { label }]) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => addTemplate(key)}
          >
            <FileJson className="h-3 w-3" />
            {label}
          </Button>
        ))}
      </div>
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={handleBlur}
        rows={12}
        className="font-mono text-xs"
        placeholder='[{ "@context": "https://schema.org", "@type": "Organization", ... }]'
      />
      <div className="flex items-center justify-between">
        {error ? (
          <span className="text-xs text-destructive">{error}</span>
        ) : (
          <span className="text-[10px] text-muted-foreground/80">
            {itemCount} structured data block(s)
          </span>
        )}
        {itemCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-destructive h-7"
            onClick={() => {
              onChange([]);
              setRaw('[]');
            }}
          >
            Limpar todos
          </Button>
        )}
      </div>
    </div>
  );
}

function SecaoDialog({
  open,
  onOpenChange,
  secao,
  paginaId: _paginaId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  secao: PortalSecao | null;
  paginaId: string;
}) {
  const [form, setForm] = useState({
    slug_secao: '',
    titulo: '',
    subtitulo: '',
    tipo: 'texto',
    conteudo: '{}',
    ordem: 0,
    ativo: true,
  });

  useEffect(() => {
    if (open) {
      if (secao) {
        setForm({
          slug_secao: secao.slug_secao,
          titulo: secao.titulo || '',
          subtitulo: secao.subtitulo || '',
          tipo: secao.tipo,
          conteudo: JSON.stringify(secao.conteudo, null, 2),
          ordem: secao.ordem,
          ativo: secao.ativo,
        });
      } else {
        setForm({
          slug_secao: '',
          titulo: '',
          subtitulo: '',
          tipo: 'texto',
          conteudo: '{}',
          ordem: 0,
          ativo: true,
        });
      }
    }
  }, [secao, open]);

  const handleSave = () => {
    try {
      JSON.parse(form.conteudo);
    } catch {
      toast.error('JSON inválido no campo conteúdo');
      return;
    }
    avisarAcaoIndisponivel();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{secao ? 'Editar seção' : 'Nova seção'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormFieldWrapper label="Slug da seção" required>
              <Input
                value={form.slug_secao}
                onChange={(e) => setForm({ ...form, slug_secao: e.target.value })}
                placeholder="ex: modalidades"
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['texto', 'cards', 'lista', 'faq', 'timeline', 'stats', 'citacao', 'tabela'].map(
                    (t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormFieldWrapper label="Título">
              <Input
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Ordem">
              <Input
                type="number"
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })}
              />
            </FormFieldWrapper>
          </div>
          <FormFieldWrapper label="Subtítulo">
            <Input
              value={form.subtitulo}
              onChange={(e) => setForm({ ...form, subtitulo: e.target.value })}
            />
          </FormFieldWrapper>
          <FormFieldWrapper
            label="Conteúdo (JSON)"
            hint="Estrutura JSON conforme o tipo selecionado"
          >
            <Textarea
              value={form.conteudo}
              onChange={(e) => setForm({ ...form, conteudo: e.target.value })}
              rows={10}
              className="font-mono text-xs"
            />
          </FormFieldWrapper>
          <div className="flex items-center gap-2">
            <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
            <Label className="text-sm">Seção ativa</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalPaginaEditor() {
  const { slug } = useParams<{ slug: string }>();
  const { data: pagina, isLoading } = usePortalPagina(slug || '');
  const { data: secoes, isLoading: secoesLoading } = usePortalSecoes(pagina?.id);
  const [seoOpen, setSeoOpen] = useState(false);
  const [jsonldOpen, setJsonldOpen] = useState(false);
  const [secaoDialogOpen, setSecaoDialogOpen] = useState(false);
  const [editingSecao, setEditingSecao] = useState<PortalSecao | null>(null);

  const [form, setForm] = useState({
    titulo: '',
    hero_titulo: '',
    hero_descricao: '',
    hero_imagem_url: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image_url: '',
    canonical_url: '',
    noindex: false,
    ativo: true,
    jsonld_data: [] as JsonLdEntry[],
  });

  useEffect(() => {
    if (pagina) {
      setForm({
        titulo: pagina.titulo || '',
        hero_titulo: pagina.hero_titulo || '',
        hero_descricao: pagina.hero_descricao || '',
        hero_imagem_url: pagina.hero_imagem_url || '',
        meta_title: pagina.meta_title || '',
        meta_description: pagina.meta_description || '',
        meta_keywords: pagina.meta_keywords || '',
        og_image_url: pagina.og_image_url || '',
        canonical_url: pagina.canonical_url || '',
        noindex: pagina.noindex || false,
        ativo: pagina.ativo,
        jsonld_data: pagina.jsonld_data || [],
      });
    }
  }, [pagina]);

  if (isLoading) return <Spinner size="lg" fullPage />;
  if (!pagina)
    return <div className="p-8 text-center text-muted-foreground">Página não encontrada</div>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    avisarAcaoIndisponivel();
  };

  return (
    <div className="space-y-6">
      <FormPage
        onSubmit={handleSubmit}
        isSaving={false}
        backTo="/admin/portal/paginas"
        submitLabel="Salvar página"
      >
        <FormSection title="Conteúdo da página" subtitle={`Slug: /${slug}`} icon={Globe}>
          <FormFieldWrapper label="Título">
            <Input
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            />
          </FormFieldWrapper>
          <FormFieldWrapper label="Hero — Título">
            <Input
              value={form.hero_titulo}
              onChange={(e) => setForm({ ...form, hero_titulo: e.target.value })}
            />
          </FormFieldWrapper>
          <FormFieldWrapper label="Hero — Descrição">
            <Textarea
              value={form.hero_descricao}
              onChange={(e) => setForm({ ...form, hero_descricao: e.target.value })}
              rows={3}
            />
          </FormFieldWrapper>
          <FormFieldWrapper label="Hero — URL da imagem" hint="URL da imagem de fundo do hero">
            <Input
              value={form.hero_imagem_url}
              onChange={(e) => setForm({ ...form, hero_imagem_url: e.target.value })}
              placeholder="https://..."
            />
          </FormFieldWrapper>
          <div className="flex items-center gap-2">
            <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
            <Label className="text-sm">Página ativa</Label>
          </div>
        </FormSection>

        <Collapsible open={seoOpen} onOpenChange={setSeoOpen}>
          <FormSection title="SEO & Mecanismos de Busca" icon={Search} className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="w-full justify-between text-xs text-muted-foreground"
              >
                {seoOpen ? 'Recolher' : 'Expandir campos de SEO'}
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', seoOpen && 'rotate-180')}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-5 pt-2">
              <FormFieldWrapper
                label="Meta Title"
                hint="Título para buscadores (recomendado: até 60 caracteres)"
                trailing={<CharCounter value={form.meta_title} max={60} />}
              >
                <Input
                  value={form.meta_title}
                  onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                  placeholder={form.titulo}
                />
              </FormFieldWrapper>
              <FormFieldWrapper
                label="Meta Description"
                hint="Descrição para buscadores (recomendado: até 160 caracteres)"
                trailing={<CharCounter value={form.meta_description} max={160} />}
              >
                <Textarea
                  value={form.meta_description}
                  onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  rows={2}
                  placeholder={form.hero_descricao}
                />
              </FormFieldWrapper>
              <FormFieldWrapper label="Meta Keywords" hint="Palavras-chave separadas por vírgula">
                <Input
                  value={form.meta_keywords}
                  onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })}
                  placeholder="transporte escolar, TEG, prefeitura"
                />
              </FormFieldWrapper>
              <FormFieldWrapper
                label="OG Image URL"
                hint="Imagem para compartilhamento em redes sociais"
              >
                <div className="flex gap-2">
                  <Input
                    value={form.og_image_url}
                    onChange={(e) => setForm({ ...form, og_image_url: e.target.value })}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  {form.og_image_url && (
                    <div className="h-11 w-16 rounded-lg border bg-muted overflow-hidden shrink-0">
                      <img src={form.og_image_url} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
              </FormFieldWrapper>
              <FormFieldWrapper
                label="Canonical URL"
                hint="URL canônica para evitar conteúdo duplicado (opcional)"
              >
                <Input
                  value={form.canonical_url}
                  onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                  placeholder="https://..."
                />
              </FormFieldWrapper>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.noindex}
                  onCheckedChange={(v) => setForm({ ...form, noindex: v })}
                />
                <div>
                  <Label className="text-sm">Noindex</Label>
                  <p className="text-[10px] text-muted-foreground/70">
                    Impede que buscadores indexem esta página
                  </p>
                </div>
              </div>
              <SerpPreview
                title={form.meta_title || form.titulo}
                description={form.meta_description || form.hero_descricao}
                slug={slug || ''}
              />
            </CollapsibleContent>
          </FormSection>
        </Collapsible>

        <Collapsible open={jsonldOpen} onOpenChange={setJsonldOpen}>
          <FormSection title="Structured Data (JSON-LD)" icon={Code2} className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="w-full justify-between text-xs text-muted-foreground"
              >
                {jsonldOpen ? 'Recolher' : 'Expandir structured data'}
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', jsonldOpen && 'rotate-180')}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <p className="text-xs text-muted-foreground mb-3">
                Adicione blocos de dados estruturados (schema.org) para melhorar a exibição nos
                resultados de busca. Use os templates para começar rapidamente.
              </p>
              <JsonLdEditor
                value={form.jsonld_data}
                onChange={(v) => setForm({ ...form, jsonld_data: v })}
              />
            </CollapsibleContent>
          </FormSection>
        </Collapsible>

        <FormSection title="Seções de conteúdo" icon={Globe}>
          <div className="flex justify-end mb-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setEditingSecao(null);
                setSecaoDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> Nova seção
            </Button>
          </div>
          {secoesLoading ? (
            <Spinner />
          ) : !secoes?.length ? (
            <EmptyState
              title="Nenhuma seção"
              description="Adicione seções de conteúdo a esta página."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Ordem</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Título</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {secoes.map((s) => (
                        <TableRow
                          key={s.id}
                          className="cursor-pointer"
                          onClick={() => {
                            setEditingSecao(s);
                            setSecaoDialogOpen(true);
                          }}
                        >
                          <TableCell>{s.ordem}</TableCell>
                          <TableCell className="font-mono text-xs">{s.slug_secao}</TableCell>
                          <TableCell className="text-xs">{s.tipo}</TableCell>
                          <TableCell>
                            {s.titulo || (
                              <span className="text-muted-foreground/80 italic text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={s.ativo ? 'ativo' : 'inativo'} />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSecao(s);
                                  setSecaoDialogOpen(true);
                                }}
                                aria-label="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <ConfirmDialog
                                trigger={
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Excluir"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                }
                                title="Excluir seção?"
                                description={`A seção "${s.slug_secao}" será removida permanentemente.`}
                                variant="destructive"
                                onConfirm={() => avisarAcaoIndisponivel()}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </FormSection>

        <SecaoDialog
          open={secaoDialogOpen}
          onOpenChange={setSecaoDialogOpen}
          secao={editingSecao}
          paginaId={pagina.id}
        />
      </FormPage>
    </div>
  );
}
